import { Components } from '@ionic/core/dist/types/components';
import {
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardTitle,
  IonPopover,
  useIonAlert,
} from '@ionic/react';
import { Button, MobileStepper } from '@mui/material';
import { clamp, isNil } from 'lodash';
import {
  ReactElement,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from 'react-icons/md';
import { PlanEntitlementKey } from '../../api/graphql/generated';
import {
  useGetFeatureTourPreference,
  useSetFeatureTourPreference,
} from '../../api/hooks/feature-tour.hooks';
import { trackFeatureTourStep } from '../../config/analytics';
import { getLocalizedText } from '../../i18n/i18n.utils';
import { runInBackground } from '../../util/async';
import { usePrevious } from '../../util/hooks';
import { logger } from '../../util/logger';
import { useHasEntitlementLazy } from '../plan/plan-entitlement-hooks';
import { AppFeatureKey, FeatureTourPromotionDetails } from './feature.types';

export interface FeatureTourStep {
  analyticKey: string;
  selector: string;
  title: string;
  description: ReactElement | string;
  position?: Pick<Components.IonPopover, 'side' | 'alignment'>;
  disableHighlight?: boolean;
}

export interface FeatureTourProps {
  appFeatureKey: AppFeatureKey;
  planEntitlementKey?: PlanEntitlementKey;
  steps: FeatureTourStep[];
  onClose?: () => void;
  popoverProps?: Partial<Components.IonPopover>;
  disableTourAlert?: boolean;
}

export interface FeatureTourRef {
  startTour: (index?: number) => void;
  stopTour: () => void;
}

const FeatureTour = forwardRef<FeatureTourRef, FeatureTourProps>(
  (props: FeatureTourProps, ref) => {
    const {
      onClose,
      popoverProps,
      steps,
      appFeatureKey,
      planEntitlementKey,
      disableTourAlert,
    } = props;

    const [isPopoverOpen, setPopoverOpen] = useState<boolean>(false);
    const [activeStepIndex, setActiveStepIndex] = useState<number>(-1);

    const hasEntitlement = useHasEntitlementLazy();
    const isEntitled = planEntitlementKey
      ? hasEntitlement(planEntitlementKey)
      : true;

    const isMarkingAsInteracted = useRef(false);
    const setFeatureTourPreference = useSetFeatureTourPreference();
    const featureTourPreference = useGetFeatureTourPreference(appFeatureKey);
    const { seen: hasSeenTour = false, skipped: hasSkippedTour = false } =
      featureTourPreference ?? {};
    const hasInteractedWithTour = hasSeenTour || hasSkippedTour;

    const { i18n } = useTranslation();

    const activeStep = useMemo(() => {
      let step;

      if (activeStepIndex > -1) {
        step = steps[activeStepIndex];
      }

      if (step !== undefined) {
        trackFeatureTourStep(step.analyticKey);
      }

      return step;
    }, [steps, activeStepIndex]);

    const prevStep = usePrevious(activeStep);

    const styleRef = useRef<HTMLStyleElement | undefined>();

    useImperativeHandle(ref, () => {
      return {
        startTour: (index: number = 0) => {
          logger.info('starting tour', { index });

          if (index !== undefined && index >= 0 && index < steps.length) {
            setActiveStepIndex(index);
          }

          setPopoverOpen(true);
        },
        stopTour: () => {
          logger.info('stopping tour');
          onDidDismiss();
        },
      };
    });

    const handleNext = useCallback(() => {
      setActiveStepIndex((prevActiveStep) =>
        clamp(prevActiveStep + 1, 0, steps.length - 1)
      );
    }, [steps]);

    const handleBack = useCallback(() => {
      setActiveStepIndex((prevActiveStep) => Math.max(prevActiveStep - 1, 0));
    }, []);

    const removeHighlightStyle = useCallback(() => {
      if (styleRef.current) {
        try {
          document.head.removeChild(styleRef.current);
        } catch (err) {
          // do nothing
        }
      }
    }, []);

    const addHighlightStyle = useCallback((selector: string) => {
      const style = document.createElement('style');

      style.innerHTML = `
          ${selector}::after {
            content: "";
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            -webkit-backdrop-filter: brightness(2);
            backdrop-filter: brightness(2);
          }
        `;

      document.head.appendChild(style);

      styleRef.current = style;
    }, []);

    useEffect(() => {
      if (activeStep !== undefined) {
        const activeStepIdSelector = `#${activeStep.selector}`;

        removeHighlightStyle();

        if (isPopoverOpen && !activeStep?.disableHighlight) {
          addHighlightStyle(activeStepIdSelector);
        }
      }
    }, [
      activeStep,
      prevStep,
      isPopoverOpen,
      removeHighlightStyle,
      addHighlightStyle,
    ]);

    const [presentAlert, dismissAlert] = useIonAlert();

    const onDidDismiss = useCallback(() => {
      logger.info('dismissing feature tour popover');

      removeHighlightStyle();
      dismissAlert();
      setPopoverOpen(false);
      setActiveStepIndex(-1);

      if (typeof onClose === 'function') {
        onClose();
      }
    }, [dismissAlert, onClose, removeHighlightStyle]);

    const markTourAsSeen = useCallback(async () => {
      if (!hasSeenTour) {
        isMarkingAsInteracted.current = true;
        try {
          await setFeatureTourPreference(appFeatureKey, {
            seen: true,
          });
        } catch (error) {
          logger.error('failed to mark tour as seen', { appFeatureKey, error });
        } finally {
          isMarkingAsInteracted.current = false;
        }
      }
    }, [appFeatureKey, hasSeenTour, setFeatureTourPreference]);

    const popoverKey = useMemo(() => {
      return `popover-${activeStep?.selector}-${activeStepIndex}`;
    }, [activeStep, activeStepIndex]);

    const isLastStep = activeStepIndex === steps.length - 1;

    const popoverPositionalProps:
      | Pick<Components.IonPopover, 'side' | 'alignment'>
      | undefined = useMemo(() => {
      return activeStep?.position ?? undefined;
    }, [activeStep?.position]);

    const displayTourAlert =
      !isMarkingAsInteracted.current &&
      !disableTourAlert &&
      isEntitled &&
      !isNil(featureTourPreference) &&
      !hasInteractedWithTour &&
      activeStepIndex === -1;

    useEffect(() => {
      if (displayTourAlert) {
        presentAlert({
          header: getLocalizedText(
            FeatureTourPromotionDetails[appFeatureKey].title
          ),
          message: getLocalizedText(
            FeatureTourPromotionDetails[appFeatureKey].description ??
              'common:feature_tour_prompt'
          ),
          buttons: [
            {
              text: getLocalizedText('common:skip_tour') ?? 'Skip Tour',
              role: 'cancel',
              handler: () => {
                runInBackground(async () => {
                  logger.info('skipping tour from feature alert');
                  isMarkingAsInteracted.current = true;
                  await setFeatureTourPreference(appFeatureKey, {
                    skipped: true,
                  });
                });
              },
            },
            {
              text: getLocalizedText('common:start_tour') ?? 'Start Tour',
              handler: () => {
                logger.info('starting tour from alert', {
                  index: 0,
                });
                setActiveStepIndex(0);
                setPopoverOpen(true);
              },
            },
          ],
          backdropDismiss: false,
        });
      }

      return () => {
        dismissAlert();
      };
    }, [
      appFeatureKey,
      dismissAlert,
      displayTourAlert,
      presentAlert,
      setFeatureTourPreference,
    ]);

    return activeStep ? (
      <IonPopover
        key={popoverKey}
        {...(popoverProps ?? {})}
        trigger={activeStep.selector}
        reference="trigger"
        isOpen={isPopoverOpen}
        onDidDismiss={onDidDismiss}
        keepContentsMounted={true}
        {...popoverPositionalProps}
      >
        <IonCard>
          <IonCardHeader>
            <IonCardTitle>{activeStep?.title}</IonCardTitle>
          </IonCardHeader>
          <IonCardContent>{activeStep?.description}</IonCardContent>
        </IonCard>
        <MobileStepper
          steps={steps.length}
          activeStep={activeStepIndex}
          nextButton={
            <Button
              size="small"
              onClick={() => {
                if (isLastStep) {
                  markTourAsSeen();
                  onDidDismiss();
                } else {
                  handleNext();
                }
              }}
            >
              {isLastStep ? i18n.t('common:close') : i18n.t('common:next')}
              {!isLastStep && <MdKeyboardArrowRight />}
            </Button>
          }
          backButton={
            <Button
              size="small"
              onClick={handleBack}
              disabled={activeStepIndex === 0}
            >
              <MdKeyboardArrowLeft />
              {i18n.t('common:prev')}
            </Button>
          }
          position="static"
        />
      </IonPopover>
    ) : null;
  }
);

FeatureTour.displayName = 'FeatureTour';

export { FeatureTour };
